import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { fetchTermsStatus, acceptTerms, logout } from '@/lib/api';
import { TermsAcceptCard } from './TermsAcceptCard';
import styles from './TermsGate.module.css';

/**
 * Blocking terms-acceptance gate for the driver app. On an authenticated
 * load it fetches the doc types that APPLY to this user — driver_tos always,
 * plus payout_terms once they are an earner (Connect account or SP) and
 * sp_terms for Sales Partners — and blocks on the first unaccepted one,
 * rendering a full-screen accept card over everything (including the guided
 * tour). Accepting advances to the next unaccepted doc; a version bump
 * re-triggers automatically (the server reports accepted:false against the
 * new version).
 *
 * Mounted once at the top of the authenticated tree. The only escape besides
 * accepting is signing out — acceptance is a deliberate act, so there is no
 * dismiss.
 */
const APPLICABLE_QUERY_KEY = ['terms', 'status', 'applicable'] as const;

/**
 * The gate's state, shared with the map so it can hold its location
 * request until the driver is past the gate (a permission prompt stacked
 * on the terms card is the wrong moment to ask). Same query, same cache
 * entry as the gate itself, so it adds no request.
 *   open:    the gate is covering the app (a document to accept, or blocked).
 *   settled: the gate's answer is known (or the driver is signed out), so
 *            "not open" really means the way is clear.
 */
export function useTermsGateState() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const q = useQuery({
    queryKey: APPLICABLE_QUERY_KEY,
    // No docs arg → the server resolves the applicable set for this user.
    queryFn: () => fetchTermsStatus(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  // First unaccepted applicable doc, in the server's order (driver_tos first).
  const doc = q.data?.find((d) => !d.accepted);

  // BLOCKED WITH NOTHING TO SHOW. Two ways to get here, and both used to let
  // the driver straight through:
  //   - the status request failed (offline, 500, expired session mid-flight),
  //     so q.data is undefined, `doc` is undefined, and the gate rendered null;
  //   - the server resolved the doc but has no current version for it, and
  //     reports it unavailable.
  // A gate that opens on a failed request can be opened ON PURPOSE by making
  // the request fail. Block instead, and say why.
  const blockedUnresolved = isAuthenticated
    && (q.isError || (q.isSuccess && !q.data) || Boolean(doc?.unavailable));

  return {
    isAuthenticated,
    q,
    doc,
    blockedUnresolved,
    open: isAuthenticated && (blockedUnresolved || Boolean(doc)),
    settled: !isAuthenticated || q.isSuccess || q.isError,
  };
}

export function TermsGate() {
  const { isAuthenticated, q, doc, blockedUnresolved } = useTermsGateState();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const qc = useQueryClient();

  const accept = useMutation({
    mutationFn: (docType: string) => acceptTerms(docType as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: APPLICABLE_QUERY_KEY }),
  });

  const onSignOut = useCallback(async () => {
    try { await logout(); } catch { /* ignore */ }
    clearAuth();
    window.location.assign('/login');
  }, [clearAuth]);

  // Still loading on first paint is NOT blocked: with no answer yet there is
  // nothing to say, and flashing an error card during a normal cold start
  // would be worse than waiting. Only a settled failure blocks.
  if (!isAuthenticated) return null;
  if (!blockedUnresolved && !doc) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-terms-title">
      {blockedUnresolved || !doc ? (
        <TermsUnavailableCard
          onRetry={() => void q.refetch()}
          isRetrying={q.isFetching}
          onSignOut={onSignOut}
        />
      ) : (
        <TermsAcceptCard
          // Keyed on the document so the card REMOUNTS per doc: fresh scroll
          // position, unticked checkbox, scroll-to-end requirement re-armed.
          // Unkeyed, React reused one instance across the sequence and each
          // next document opened wherever the previous one was left.
          key={`${doc.doc_type}:${doc.version}`}
          doc={doc}
          eyebrow="Please review to continue"
          dismissLabel="Sign out"
          isPending={accept.isPending}
          isError={accept.isError}
          onAccept={() => accept.mutate(doc.doc_type)}
          onDismiss={onSignOut}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * Shown when the gate cannot establish whether the driver has accepted the
 * current terms. Deliberately offers Try again and Sign out, and NOTHING that
 * continues into the app: "continue anyway" would restore the exact fail-open
 * this replaces.
 */
function TermsUnavailableCard(
  { onRetry, isRetrying, onSignOut }:
  { onRetry: () => void; isRetrying: boolean; onSignOut: () => void },
) {
  const { t } = useTranslation('chrome');
  return (
    <div className={styles.card}>
      <p className={styles.eyebrow}>{t('terms.unavailableEyebrow')}</p>
      <h2 className={styles.title} id="evi-terms-title">{t('terms.unavailableTitle')}</h2>
      <p className={styles.unavailableBody}>{t('terms.unavailableBody')}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.ghost} onClick={onSignOut}>
          {t('terms.signOut')}
        </button>
        <button type="button" className={styles.primary} onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? t('terms.retrying') : t('terms.retry')}
        </button>
      </div>
    </div>
  );
}
